// @ts-strict-ignore
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import mapKeys from 'lodash/mapKeys';

import {Alignment, AlignmentOptionsTrackingLabel} from 'common/widgets/sankey-chart/models/types';
import {DEFAULT_FLOWS_MATH, ProjectFeatureFlag, ResourceType, SHOW_LAST_X_DAYS_VALUES} from 'common/report/constants';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {getSegmentType, isCohortSegment, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {
  FiltersOperator,
  FlowsQueryEventEntry,
  QueryEventEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {fromEnum, isAnyOrderFunnel} from 'common/report/funnels/util';
import {bookmarkGroupByToGroupBy, groupByToBookmarkGroupBy} from 'common/query-builder/query-block/segment-menu/util';
import {
  bookmarkFilterByCohortToCohortFilters,
  getFilterByCohorts,
} from 'common/query-builder/query-block/filter-menu/util';
import {Segmentation} from 'common/types/reports/query';
import {Property} from 'common/query-builder/query-block/properties-menu/types';
import {Filter} from 'common/query-builder/query-builder/types';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DisplayBookmark} from 'common/report/dashboards/types';
import {BookmarkFunnelStep, ConversionWindow, CountType} from 'common/report/funnels/models/types';
import {
  mapBookmarkExclusionStepToEventEntry,
  mapBookmarkStepToEventEntry,
  mapBookmarkToHoldPropConstant,
  mapEventEntryToBookmarkExclusionStep,
  mapEventEntryToBookmarkStep,
  mapHoldPropConstantToBookmark,
} from 'common/report/funnels/models/format';
import {ClientQueryTrackingProps, QueryTrackingPropName} from 'common/report/tracking-setup';
import {FunnelsCountType} from 'common/report/funnels/models/constants';
import {ConversionWindowUnit, FunnelOrder} from 'common/types/reports/bookmark';

import {
  ANCHOR_POSITION_MAP,
  CHART_TYPE_TO_QUERY_PARAM,
  ChartType,
  FlowAnchorPosition,
  FlowsBookmark,
  FunnelParamsForFlows,
} from './types';

const DEFAULT_CONV_WINDOW = {
  unit: ConversionWindowUnit.Day,
  value: 7,
};

export const FLOWS_DEFAULT_EVENT = {
  filters: [],
  event: {
    custom: false,
    label: ``,
    value: null,
  },
  type: SelectorTypeDefinitions.FlowsEvent,
  forward: 3,
};

export const FLOWS_REPORT_DEFAULTS = Object.freeze({
  displayOptions: {},
  legend: {},
  aggregateBy: [],
  alignment: [Alignment.RIGHT, Alignment.LEFT],
  alignBreakdowns: true,
  alwaysBreakdown: false,
  anchorPosition: FlowAnchorPosition.Step,
  binaryTag: null,
  cardinality: 3,
  chartType: ChartType.Sankey,
  cohortFilters: [],
  collapseRepeated: false,
  conversionWindow: DEFAULT_CONV_WINDOW,
  countType: CountType.Unique,
  dataGroupId: null,
  dateRange: new FullLengthDateRange().getAttributes(),
  description: null,
  events: [
    {
      filters: [],
      event: {
        label: ``,
        value: null,
      },
      type: ResourceType.Event,
    },
  ],
  eventFilters: [],
  exclusionSteps: [],
  funnelParams: null,
  funnelOrder: FunnelOrder.Loose,
  graphSortPriority: `absolute`,
  groupBy: [],
  hiddenEvents: [],
  holdingPropertyConstant: [],
  listSortPriority: `end`,
  maxResults: 50,
  maxSteps: -1,
  name: null,
  otherFilters: [],
  projectId: -1,
  querySampling: false,
  relativeBreakdowns: false,
  reverse: false,
  segments: [],
  showCustomEvents: true,
  timePercentiles: [],
  uniqueFlows: false,
  userFilters: [],
  version: `1`,
  visibleEvents: [],
});

/** Instantiates a PropertyFilter class from attributes */
export const constructPropertyFilters = (filterPrimitives) =>
  filterPrimitives.map((filter) => new PropertyFilter(filter));

export const BOOKMARK_PARAM_MAP = Object.freeze({
  align_breakdowns: `alignBreakdowns`,
  always_breakdown: `alwaysBreakdown`,
  collapse_repeated: `collapseRepeated`,
  hidden_events: `hiddenEvents`,
  visible_events: `visibleEvents`,
  graph_sort_priority: `graphSortPriority`,
  list_sort_priority: `listSortPriority`,
  segments: `segments`,
  show_custom_events: `showCustomEvents`,
  unique_flows: `uniqueFlows`,
  count_type: `countType`,
  conversion_window: `conversionWindow`,
  aggregate_by: `aggregateBy`,
});

const BOOKMARK_COPY_OVER_KEYS = Object.freeze([
  `displayOptions`,
  `legend`,
  `alignment`,
  `anchorPosition`,
  `funnelParams`,
  `maxSteps`,
  `maxResults`,
  `cardinality`,
  `chartType`,
  `reverse`,
]);

const FLOWS_REPORT_DEFAULTS_KEYS = Object.keys(FLOWS_REPORT_DEFAULTS);

const BOOKMARK_PARAM_MAP_KEYS = Object.keys(BOOKMARK_PARAM_MAP);
const DEFAULTS = {...FLOWS_REPORT_DEFAULTS};
const URL_PARAMS = Object.keys(DEFAULTS);

export const CARDINALITY_FOR_DOWNLOAD = 10;
export const MAX_STEPS_FOR_DOWNLOAD = 15;
export const MAX_RESULTS_FOR_DOWNLOAD = 5;

export class FlowsReport {
  //TODO (Bruna): Remove this fromBookmark function -if possible - as part of a second pass flows refactor
  public static fromBookmark(bookmark: DisplayBookmark<FlowsBookmark>): FlowsReport {
    const {params, name, description} = bookmark;

    if (!isNaN(parseInt(params.anchorPosition))) {
      params.anchorPosition = Object.values(FlowAnchorPosition)[params.anchorPosition];
    }

    const classParams = FlowsReport.bookmarkToClassParams(params);
    const convertedParams = mapKeys(pick(params, BOOKMARK_PARAM_MAP_KEYS), (_, k) => BOOKMARK_PARAM_MAP[k]);
    const copiedParams = pick(params, BOOKMARK_COPY_OVER_KEYS);

    const attrs = {
      ...classParams,
      ...convertedParams,
      ...copiedParams,
      name,
      description,
    };

    return new FlowsReport(attrs);
  }

  public static fromBookmarkParams(params: Partial<FlowsBookmark>): FlowsReport {
    if (!isNaN(parseInt(params.anchorPosition))) {
      params.anchorPosition = Object.values(FlowAnchorPosition)[params.anchorPosition];
    }

    const classParams = FlowsReport.bookmarkToClassParams(params);
    const convertedParams = mapKeys(pick(params, BOOKMARK_PARAM_MAP_KEYS), (_, k) => BOOKMARK_PARAM_MAP[k]);
    const copiedParams = pick(params, BOOKMARK_COPY_OVER_KEYS);

    const attrs = {
      ...classParams,
      ...convertedParams,
      ...copiedParams,
    };

    return new FlowsReport(attrs);
  }

  public static bookmarkToClassParams(params) {
    const attrs: Partial<FlowsReport> = {};

    if (params.steps) {
      attrs.events = params.steps.map(mapBookmarkStepToEventEntry.bind(null));
    }
    if (params.exclusions) {
      attrs.exclusionSteps = params.exclusions.map(
        mapBookmarkExclusionStepToEventEntry.bind(null, attrs.events.length),
      );
    }

    if (params.group_by) {
      attrs.groupBy = bookmarkGroupByToGroupBy(params.group_by);
    }

    // TODO: Split this into cohortFilter and userFilters when we switch over to the new API
    attrs.cohortFilters = bookmarkFilterByCohortToCohortFilters(params.filter_by_cohort);

    if (params.date_range) {
      attrs.dateRange = FullLengthDateRange.fromBookmarkFormat(params.date_range).getAttributes();
    }

    if (params.filter_by_event) {
      attrs.eventFilters = params.filter_by_event.children.filter((child) => child.event).map((filter) => filter.event);
      attrs.userFilters = params.filter_by_event.children.filter((child) => child.user).map((filter) => filter.user);
      attrs.otherFilters = params.filter_by_event.children.filter((child) => child.other).map((filter) => filter.other);
    }

    if (params.data_group_id) {
      attrs.dataGroupId = params.data_group_id;
    }

    if (params.time_percentiles_enabled) {
      attrs.timePercentiles = [50.0];
    }

    if (params.graph_sort_priority === `relative`) {
      attrs.relativeBreakdowns = true;
    }

    return attrs;
  }

  public getEventsAsFlowQueryEntries(): FlowsQueryEventEntry[] {
    return this._events.map((event) => {
      return {type: SelectorTypeDefinitions.FlowsEvent, ...omit(event, [`type`])};
    });
  }

  get events() {
    return this._events;
  }

  set events(events: FlowsQueryEventEntry[]) {
    // The current implementation for alignment works for the toggle all alignment implementation.
    // If we later switch to controling the alignment per section, this implementation will have to be revisited.
    if (!this.alignment || this.alignment.length === 0) {
      // Reverse steps of the first anchor are always right aligned.
      this.alignment = [Alignment.RIGHT].concat(new Array<Alignment>(events.length).fill(Alignment.LEFT));
    } else if (events.length > 0 && events.length !== this.alignment.length - 1) {
      // Forward steps of the last anchor are always left aligned.
      const alignmentFill = this.getOverallAlignment();
      this.alignment = [Alignment.RIGHT]
        .concat(new Array<Alignment>(events.length - 1).fill(alignmentFill))
        .concat([Alignment.LEFT]);
    }
    this._events = this.generateFlowsEvents(events);
  }

  get eventSelection() {
    let eventSelection = [];
    if (this.events) {
      eventSelection = this.events.filter(Boolean);
    }
    return eventSelection;
  }

  get previousEventSelection() {
    return this._previousEventSelection;
  }

  get cohortGroupBys() {
    return this.groupBy.filter(isCohortSegment);
  }

  set funnelParams(funnelParams: FunnelParamsForFlows) {
    if (funnelParams && funnelParams.funnelBookmark && funnelParams.funnelBookmark[`count_type`]) {
      this.countType = fromEnum(funnelParams.funnelBookmark[`count_type`], CountType, CountType.Unique);
    }
    if (funnelParams && funnelParams.funnelBookmark && funnelParams.funnelBookmark[`aggregate_by`]) {
      this.aggregateBy = mapBookmarkToHoldPropConstant(funnelParams.funnelBookmark[`aggregate_by`]);
    }
    this._funnelParams = funnelParams;
  }

  get funnelParams() {
    return this._funnelParams;
  }

  getTrackingProps(): ClientQueryTrackingProps {
    return {
      'Anchor Events': this.eventSelection.map((entry: QueryEventEntry) => entry.event.label),
      'Custom event': this.eventSelection.some((entry: QueryEventEntry) => entry.event.custom),
      'Event type': this.eventSelection.map((entry: QueryEventEntry) =>
        entry.event.custom ? `Custom event` : `Event`,
      ),
      [QueryTrackingPropName.ChartType]: this.chartType,
      reverse: !!this.reverse,
      '# of filters': this.filters.length,
      '# of breakdowns': this.segments.length,
      '# of hidden events': this.hiddenEvents.length,
      '# of visible events': this.visibleEvents.length,
      '# of cohort filters': this.cohortFilters.length,
      '# of cohort segments': this.groupBy.map((segment) => getSegmentType(segment) === ResourceType.Cohort).length,
      'Query time Sampling': this.querySampling,
      Alignment: AlignmentOptionsTrackingLabel[this.getOverallAlignment()],
      '# of steps': this.eventSelection
        .map((event) => (event[`forward`] || 0) + (event[`reverse`] || 0))
        .reduce((acc, val) => acc + val, 0),
      '# of anchor events': this.eventSelection.length,
      'Time percetiles': this.timePercentiles.length,
      'Collapse Repeated': this.collapseRepeated,
      Cardinality: this.cardinality,
      ...(this.maxSteps === -1 ? {'Flows Version': `anchor steps`} : {}),
      'Drop off analysis mode': this.containsFunnelBookmark(),
      'Show custom events': this.showCustomEvents,
    };
  }

  // These is actually PropertyFilter[], but the report transform partial filters into property filters
  // so we say it's partial to make TS happy. TODO: stop transforming stuff in getters/setters
  get filters(): Filter[] {
    return [
      ...this.eventFilters,
      ...this.userFilters,
      ...this.cohortFilters,
      ...(this.otherFilters ? this.otherFilters : []),
    ];
  }

  set filters(filters: Filter[]) {
    this.eventFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Event));
    this.userFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.User));
    this.cohortFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Cohort));
    this.otherFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Other));
  }

  public declare displayOptions: FlowsBookmark[`displayOptions`];
  public declare legend: FlowsBookmark[`legend`];
  public declare aggregateBy: Segmentation[];
  public declare alignment: Alignment[];
  public declare alignBreakdowns: boolean;
  public declare alwaysBreakdown: boolean;
  public declare anchorPosition: string;
  public declare cardinality: number;
  public declare chartType: ChartType;
  public declare cohortFilters: PropertyFilter[];
  public declare collapseRepeated: boolean;
  public declare countType: CountType;
  public declare conversionWindow: ConversionWindow;
  public declare dashboard: DashboardOrigin;
  public declare dataGroupId: string;
  public declare dateRange: Partial<FullLengthDateRange>;
  public declare description: string;
  public declare eventFilters: PropertyFilter[];
  public declare exclusionSteps: QueryEventEntry[];
  public declare _funnelParams: FunnelParamsForFlows;
  public declare funnelOrder: FunnelOrder;
  public declare graphSortPriority: string;
  public declare groupBy: Segment[];
  public declare hiddenEvents: string[];
  public declare listSortPriority: string;
  public declare maxResults: number;
  public declare maxSteps: number;
  public declare name: string;
  public declare otherFilters: PropertyFilter[];
  public declare relativeBreakdowns: boolean;
  public declare reverse: boolean;
  public declare segments: Array<{
    event: EventItem;
    property: Property;
  }>;
  public declare showCustomEvents: boolean;
  // TODO: change timePercentiles to a boolean when Flows API is dropped
  public declare timePercentiles: number[];
  public declare uniqueFlows: boolean;
  public declare version: number;
  public declare visibleEvents: string[];

  private declare _events: FlowsQueryEventEntry[];
  private declare _previousEventSelection: FlowsQueryEventEntry[];

  /** transient, should not be persisted */
  public declare querySampling: boolean;
  public declare userFilters: PropertyFilter[];

  constructor(attrs: Partial<FlowsReport> = {}) {
    Object.assign(this, FLOWS_REPORT_DEFAULTS);
    Object.assign(this, pick(attrs, FLOWS_REPORT_DEFAULTS_KEYS));
    if (attrs.countType === FunnelsCountType.Session) {
      this.conversionWindow = {
        unit: ConversionWindowUnit.Session,
        value: 1,
      };
    }

    // Instantiate models from primitive attrs
    this.dateRange = new FullLengthDateRange(this.dateRange);
    this.events = this.events.map((entry) => ({...entry, filters: constructPropertyFilters(entry.filters)}));
    this.exclusionSteps = this.exclusionSteps.map((entry) => ({
      ...entry,
      filters: constructPropertyFilters(entry.filters),
    }));

    if (`filters` in attrs) {
      this.filters = constructPropertyFilters(attrs[`filters`]);
    }
  }

  public serialize(): Partial<FlowsReport> {
    return pick(this, FLOWS_REPORT_DEFAULTS_KEYS);
  }

  public clone(attrs: Partial<FlowsReport> = {}) {
    return new FlowsReport({
      ...this.serialize(),
      ...attrs,
    });
  }

  /**
   * Calculate which params are non-default, and serialize any
   * complex values (like dateRange)
   */
  public toUrlData() {
    const urlParams = URL_PARAMS.reduce((params, key) => {
      if (
        key !== `dateRange` ? !isEqual(this[key], DEFAULTS[key]) : !isEqual(this[key]?.getAttributes(), DEFAULTS[key])
      ) {
        params[key] = this[key];
      }
      return params;
    }, {});

    return isEmpty(urlParams)
      ? null
      : {
          ...urlParams,
          dateRange: this.dateRange.getAttributes(),
          version: this.version,
        };
  }

  public paramEquals(param, value) {
    switch (param) {
      case `dateRange`:
        return this.dateRange.equals(value);
      case `events`:
        return isEqual(this.events, this.generateFlowsEvents(value));
      default:
        return isEqual(this[param], value);
    }
  }

  public update(params: Partial<FlowsReport> = {}): boolean {
    let changed = false;

    // save the previous event selection before updating the report
    this._previousEventSelection = JSON.parse(JSON.stringify(this.eventSelection));

    for (const [k, v] of Object.entries(params)) {
      if (!this.paramEquals(k, v)) {
        changed = true;
        this[k] = v;
      }
    }

    if (`dateRange` in params) {
      this.dateRange = new FullLengthDateRange(params.dateRange);
    }

    // updating maxSteps requires re-generating the
    // events structure as it is derived from maxSteps
    if ((`maxSteps` in params && this.maxSteps !== params.maxSteps) || `reverse` in params) {
      // eslint-disable-next-line no-self-assign
      this.events = this.events;
    }

    return changed;
  }

  public generateBookmark(): FlowsBookmark {
    const steps = this.getEventsAsBookmark();
    const exclusions = this.getExclusionStepsAsBookmark();
    const includeNegatedCohort = this.cohortGroupBys.length > 0 && this.cohortGroupBys[0].length === 1;
    const groupBy = groupByToBookmarkGroupBy(this.groupBy, includeNegatedCohort);
    const aggregateBy = mapHoldPropConstantToBookmark(this.aggregateBy);

    const bookmark: FlowsBookmark = {
      displayOptions: this.displayOptions,
      ...(Object.keys(this.legend?.overrides || {}).length > 0 ? {legend: this.legend} : {}),
      ...(aggregateBy && aggregateBy.length ? {aggregate_by: aggregateBy} : {}),
      anchorPosition: this.anchorPosition,
      anchor_position: ANCHOR_POSITION_MAP[this.anchorPosition],
      ...(this.alignBreakdowns === false ? {align_breakdowns: this.alignBreakdowns} : {}), //alignBreakdowns defaults to true; include in bookmark if false
      ...(this.alwaysBreakdown ? {always_breakdown: this.alwaysBreakdown} : {}),
      cardinality: this.cardinality,
      maxResults: this.maxResults,
      cardinality_threshold: this.chartType === ChartType.Sankey ? this.cardinality : this.maxResults,
      collapse_repeated: this.collapseRepeated,
      ...(this.countType ? {count_type: this.countType} : {}),
      ...(this.dataGroupId ? {data_group_id: this.dataGroupId} : {}),
      date_range: this.dateRange.getBookmarkFormat(),
      exclusions,
      filter_by_cohort: JSON.stringify(this.cohortFilters.length ? getFilterByCohorts(this.cohortFilters) : {}),
      ...(this.hasFilterByEvent() ? {filter_by_event: this.getFilterByEvent()} : {}),
      flows_merge_type: CHART_TYPE_TO_QUERY_PARAM[this.chartType],
      ...(this.funnelParams ? {funnelParams: this.funnelParams} : {}),
      ...(!isEmpty(groupBy) ? {group_by: groupBy} : {}),
      show_custom_events: this.showCustomEvents,
      segments: this.segments,
      steps,
      ...(this.timePercentiles.length ? {time_percentiles_enabled: true} : {}),
      ...(this.uniqueFlows ? {unique_flows: this.uniqueFlows} : {}),
      count_type: this.countType,
      conversion_window: this.conversionWindow,
      version: this.version,
    };

    if (this.visibleEvents.length) {
      bookmark.visible_events = this.visibleEvents;
    } else {
      bookmark.hidden_events = this.hiddenEvents;
    }

    if (this.cohortGroupBys.length) {
      if (this.relativeBreakdowns) {
        bookmark.graph_sort_priority = `relative`;
      }

      if (this.chartType === ChartType.Sankey) {
        bookmark.exclude_everything_else_user_segment = `true`;
      }
    }

    if (this.chartType === ChartType.Sankey) {
      bookmark.graph_sort_priority = this.graphSortPriority;
    } else {
      bookmark.list_sort_priority = this.listSortPriority;
    }

    Object.assign(bookmark, pick(this, BOOKMARK_COPY_OVER_KEYS));

    return bookmark;
  }

  private getEventsAsBookmark(): BookmarkFunnelStep[] {
    return this.events.map((event) => mapEventEntryToBookmarkStep(event));
  }

  private getExclusionStepsAsBookmark() {
    return this.exclusionSteps.map((step) => mapEventEntryToBookmarkExclusionStep(this.events.length, step));
  }

  /**
   * Truncates left to right to create a basic preview query for download mode
   */
  public truncateForDownload() {
    let allowedSteps = MAX_STEPS_FOR_DOWNLOAD - this.events.length;
    const events = this.events.map((event) => {
      if (allowedSteps > 0) {
        const {forward = 0, reverse = 0} = event;
        if (allowedSteps - forward - reverse >= 0) {
          allowedSteps -= forward + reverse;
          return event;
        } else {
          const truncatedReverse = Math.min(reverse, allowedSteps);
          allowedSteps -= truncatedReverse;
          const truncatedForward = Math.min(forward, allowedSteps);
          allowedSteps -= truncatedForward;

          return {...event, forward: truncatedForward, reverse: truncatedReverse};
        }
      }
      return {...event, forward: 0, reverse: 0};
    });
    const cardinality = Math.min(this.cardinality, CARDINALITY_FOR_DOWNLOAD);
    const maxResults = Math.min(this.maxResults, MAX_RESULTS_FOR_DOWNLOAD);

    return this.clone({maxSteps: -1, events, cardinality, maxResults});
  }

  /**
   * Generates events with forward and reverse fields
   */
  // TODO opslead
  private generateFlowsEvents(events: FlowsQueryEventEntry[]): FlowsQueryEventEntry[] {
    // Setting maxSteps to -1 disables the legacy way of controlling steps and lets the events themselves control
    // the forward and reverse steps independently.
    if (this.maxSteps === -1) return events;

    const maxSteps = this.chartType === ChartType.Paths ? 5 : this.maxSteps || 3;
    // handle non-beta single event anchorSelection
    if (events.length === 1 && events[0]) {
      const entry = omit(events[0], `forward`, `reverse`);
      return [
        {
          ...entry,
          event: entry.event,
          [this.reverse ? `reverse` : `forward`]: maxSteps,
        },
      ];
    }

    // fill left to right direction for all anchors except last
    // allows for setting reverse flows without explicitly
    // defining the `reverse` property
    const isSparse = events.some((entry) => isEmpty(entry));

    return events.map((entry, index) => {
      if (isEmpty(entry)) {
        return null;
      }

      entry = {
        ...omit(entry, `forward`, `reverse`),
        event: entry.event,
      };

      if (this.reverse) {
        entry[`reverse`] = maxSteps;
      } else if (index === events.length - 1) {
        entry[`reverse`] = isSparse ? maxSteps : 0;
      } else {
        entry[`forward`] = maxSteps;
      }

      return entry;
    });
  }

  public hasFilterByEvent() {
    // TODO: Remove userFilters length check once we switch to the new API
    return this.eventFilters.length > 0 || this.userFilters.length > 0 || this.otherFilters.length > 0;
  }

  public getFilterByEvent() {
    return {
      operator: FiltersOperator.And,
      children: [
        ...this.eventFilters.map((filter) => ({event: filter})),
        ...this.userFilters.map((filter) => ({user: filter})),
        ...this.otherFilters.map((filter) => ({other: filter})),
      ],
    };
  }

  public getOverallAlignment() {
    return this.alignment && this.alignment.length >= 2 ? this.alignment[1] : Alignment.LEFT;
  }

  public containsFunnelBookmark(): boolean {
    return this.funnelParams !== null && this.funnelParams.funnelBookmark !== null;
  }

  public isAnyOrderFunnel() {
    return isAnyOrderFunnel(this.funnelOrder);
  }

  public getDefaultAnyOrderSteps() {
    const events = this.events.map((entry) => ({...entry, entryIndexLabelToggle: true}));
    if (events.length > 0) {
      events[0].entryIndexLabelToggle = false;
    }
    return events;
  }

  static createDefaultReport({
    showLastXDays,
    mathTypeSessions,
    disableCustomEvents,
    binaryTag,
  }: {
    showLastXDays?: ProjectFeatureFlag;
    mathTypeSessions?: ProjectFeatureFlag;
    disableCustomEvents: boolean;
    binaryTag?: string;
  }): FlowsReport {
    const attrs = {};

    if (binaryTag) {
      attrs[`binaryTag`] = binaryTag;
    }

    if (showLastXDays) {
      const value = SHOW_LAST_X_DAYS_VALUES[showLastXDays];
      if (value) {
        attrs[`dateRange`] = new FullLengthDateRange({
          type: DateRangeType.RelativeAfter,
          window: {unit: TimeUnit.Day, value},
        });
      }
    }

    if (mathTypeSessions) {
      const value = DEFAULT_FLOWS_MATH[mathTypeSessions];
      if (value) {
        attrs[`countType`] = value;
      }
    }

    if (disableCustomEvents) {
      attrs[`showCustomEvents`] = false;
    }

    return new FlowsReport(attrs);
  }
}
